import { Form, redirect, useActionData, useLoaderData, useNavigation, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import { Lines, Loading } from "../components/skeletons.jsx";
import { Deferred, ErrorState } from "../components/states.jsx";
import { translate, useI18n } from "../i18n/index.js";
import { isApiError, type ConnectorLinkRequest } from "../lib/api.js";
import { deferred } from "../lib/deferred.js";
import { apiContext } from "../lib/session.js";
import { Brand, Button, CenteredPage, FormError, LinkButton, Skeleton } from "../ui/index.js";

// Where the agent sends someone it does not recognise (D-0018): signed in here, they claim
// their chat-platform account.
export function connectLoader({ context, request }: LoaderFunctionArgs) {
  const token = new URL(request.url).searchParams.get("token") ?? "";
  const link: Promise<ConnectorLinkRequest | null> = token
    ? context
        .get(apiContext)
        .inspectConnectorLink(token)
        .catch((err: unknown) => {
          if (isApiError(err, "not_found") || isApiError(err, "not_configured")) return null;
          throw err;
        })
    : Promise.resolve(null);
  return { token, link: deferred(link) };
}

export async function connectAction({ context, request }: ActionFunctionArgs) {
  const token = String((await request.formData()).get("token") ?? "");
  try {
    const done = await context.get(apiContext).completeConnectorLink(token);
    return redirect(`/w/${done.workspace.slug}`);
  } catch (err) {
    if (isApiError(err, "not_found")) return { error: translate("connect.expired") };
    if (isApiError(err)) return { error: err.message };
    throw err;
  }
}

/** The claim: one sentence saying who is linked to what, and the button. */
function ClaimSkeleton() {
  return (
    <Loading>
      <div className="dt-form">
        <Lines count={2} />
        <Skeleton shape="button" width="100%" />
      </div>
    </Loading>
  );
}

function ConnectPage({ children }: { children: React.ReactNode }) {
  return (
    <CenteredPage>
      <div className="dt-centered__head">
        <Brand size="lg" />
      </div>
      {children}
    </CenteredPage>
  );
}

export function ConnectSkeleton() {
  return (
    <ConnectPage>
      <ClaimSkeleton />
    </ConnectPage>
  );
}

export function ConnectRoute() {
  const { token, link } = useLoaderData<typeof connectLoader>();
  return (
    <ConnectPage>
      <Deferred data={link} fallback={<ClaimSkeleton />}>
        {(link) => <Claim token={token} link={link} />}
      </Deferred>
    </ConnectPage>
  );
}

function Claim({ token, link }: { token: string; link: ConnectorLinkRequest | null }) {
  const result = useActionData<typeof connectAction>();
  const busy = useNavigation().state !== "idle";
  const { t } = useI18n();

  return (
    <>
      {link ? (
        <Form method="post" className="dt-form">
          <input type="hidden" name="token" value={token} />
          <p data-testid="connect-summary">{t("connect.summary", { provider: link.providerName, name: link.externalName, team: link.externalTeamName, workspace: link.workspace.name })}</p>
          <FormError message={result?.error ?? null} />
          <Button type="submit" variant="primary" loading={busy}>
            {t("connect.confirm")}
          </Button>
        </Form>
      ) : (
        <ErrorState
          title={t("connect.expiredTitle")}
          message={t("connect.expired")}
          action={
            <LinkButton to="/" variant="secondary" size="sm">
              {t("invitation.goToWorkspaces")}
            </LinkButton>
          }
        />
      )}
    </>
  );
}
